import React, { useEffect, useState } from "react";
import { Button, Col, DatePicker, Drawer, Form, Input, InputNumber, Row, Select, Space, Switch, TreeSelect, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { countPurchaseRecordsByDate, createPurchaseRecord, getAssetCategories, updatePurchaseRecord } from "../../api/asset";
import { getUserList } from "../../api/user";
import { getDictItems } from "../../DictCache";

const toTree = (nodes = []) => nodes.map((node) => ({ value: node._id, title: node.name, children: node.children?.length ? toTree(node.children) : undefined }));

export default function PurchaseRecordDrawer({ open, editRecord, onClose, onSuccess, onInbound }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [providers, setProviders] = useState([]);
  const orderTime = Form.useWatch("orderTime", form);
  const isEdit = !!editRecord;

  useEffect(() => {
    if (!open) return;
    Promise.all([getAssetCategories({ pageSize: 100 }), getUserList(), getDictItems("asset_procurement_source"), getDictItems("procurement_status"), getDictItems("cross_border_transit_provider")])
      .then(([categoryRes, userRes, sourceItems, statusItems, providerItems]) => {
        setCategories(toTree(categoryRes.data || []));
        setUsers((userRes.data || []).map((user) => ({ label: user?.name || user.username, value: user._id })));
        setSources(sourceItems); setStatuses(statusItems); setProviders(providerItems);
      }).catch(() => {});
    form.resetFields();
    if (editRecord) {
      form.setFieldsValue({ ...editRecord, assetCategory: editRecord.assetCategory?._id, purchaser: editRecord.purchaser?._id,
        orderTime: editRecord.orderTime ? dayjs(editRecord.orderTime) : null,
        borderArrivalTime: editRecord.borderArrivalTime ? dayjs(editRecord.borderArrivalTime) : null,
        proxySignTime: editRecord.proxySignTime ? dayjs(editRecord.proxySignTime) : null,
        pickupTime: editRecord.pickupTime ? dayjs(editRecord.pickupTime) : null });
    } else {
      let purchaser;
      try { purchaser = JSON.parse(localStorage.getItem("user") || "{}")._id; } catch {}
      form.setFieldsValue({ orderTime: dayjs(), purchaser, status: "ordered", shippingFee: 0, transitShippingFee: 0, transitShippingFeeSettled: false });
    }
  }, [open, editRecord, form]);

  useEffect(() => {
    if (!open || isEdit || !orderTime) return;
    countPurchaseRecordsByDate(orderTime).then(({ data }) => {
      form.setFieldsValue({ purchaseBatch: `PURCHASE${dayjs(orderTime).format("YYYYMMDD")}${String(data.count + 1).padStart(3, "0")}` });
    }).catch(() => {});
  }, [open, isEdit, orderTime, form]);

  const submit = async () => {
    try {
      const values = await form.validateFields(); setLoading(true);
      const payload = { ...values, orderTime: values.orderTime?.toISOString(), borderArrivalTime: values.borderArrivalTime?.toISOString(), proxySignTime: values.proxySignTime?.toISOString(), pickupTime: values.pickupTime?.toISOString() };
      if (isEdit) await updatePurchaseRecord(editRecord._id, payload); else await createPurchaseRecord(payload);
      message.success(isEdit ? "更新成功" : "创建成功"); onSuccess?.(); onClose();
    } catch (error) { if (error?.response?.data?.message) message.error(error.response.data.message); }
    finally { setLoading(false); }
  };

  return <Drawer title={isEdit ? "编辑采购记录" : "新增采购记录"} open={open} onClose={onClose} placement="right" width="100%" destroyOnClose
    extra={<Space><Button onClick={onClose}>取消</Button><Button type="primary" loading={loading} onClick={submit}>保存</Button></Space>}>
    <div style={{ maxWidth: 900, margin: "0 auto", paddingBottom: 78 }}>
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} md={8}><Form.Item name="purchaseBatch" label="采购批次" rules={[{ required: true, message: "请输入采购批次" }]}><Input /></Form.Item></Col>
          <Col xs={24} md={8}><Form.Item name="status" label="采购状态"><Select options={statuses} /></Form.Item></Col>
          <Col xs={24} md={8}><Form.Item name="orderTime" label="采购日期" rules={[{ required: true, message: "请选择下单时间" }]}><DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" /></Form.Item></Col>
          <Col xs={24} md={16}><Form.Item name="description" label="采购内容" rules={[{ required: true, message: "请输入采购内容" }]}><Input placeholder="如：宏达铁床 2×1.8 米" /></Form.Item></Col>
          <Col xs={24} md={8}><Form.Item name="assetCategory" label="资产类目" rules={[{ required: true, message: "请选择资产类目" }]}><TreeSelect showSearch treeNodeFilterProp="title" treeData={categories} /></Form.Item></Col>
          <Col xs={24} md={8}><Form.Item name="asset_procurement_source" label="采购渠道" rules={[{ required: true, message: "请选择采购渠道" }]}><Select options={sources} /></Form.Item></Col>
          <Col xs={24} md={8}><Form.Item name="purchaser" label="采购人" rules={[{ required: true, message: "请选择采购人" }]}><Select options={users} /></Form.Item></Col>
        </Row>
        <Row gutter={16}>
          <Col xs={12} md={5}><Form.Item name="unitPrice" label="采购单价（元）" rules={[{ required: true }]}><InputNumber min={0} step={0.01} style={{ width: "100%" }} /></Form.Item></Col>
          <Col xs={12} md={4}><Form.Item name="quantity" label="采购数量" rules={[{ required: true }]}><InputNumber min={1} style={{ width: "100%" }} /></Form.Item></Col>
          <Col xs={12} md={5}><Form.Item name="actualPayment" label="实付金额（元）" rules={[{ required: true }]}><InputNumber min={0} step={0.01} style={{ width: "100%" }} /></Form.Item></Col>
          <Col xs={12} md={5}><Form.Item name="shippingFee" label="运费（元）"><InputNumber min={0} step={0.01} style={{ width: "100%" }} /></Form.Item></Col>
          <Col xs={12} md={5}><Form.Item name="transitShippingFee" label="中转费（元）"><InputNumber min={0} step={0.01} style={{ width: "100%" }} /></Form.Item></Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={12}><Form.Item name="expressInfo" label="快递信息"><Input /></Form.Item></Col>
          <Col xs={24} md={12}><Form.Item name="cross_border_transit_provider" label="跨境中转公司"><Select allowClear options={providers} /></Form.Item></Col>
          <Col xs={24} md={8}><Form.Item name="borderArrivalTime" label="边境到货日期"><DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" /></Form.Item></Col>
          <Col xs={24} md={8}><Form.Item name="proxySignTime" label="代收签收日期"><DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" /></Form.Item></Col>
          <Col xs={24} md={8}><Form.Item name="pickupTime" label="最终提货日期"><DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" /></Form.Item></Col>
          <Col xs={24} md={12}><Form.Item name="transitReferenceNo" label="中转单号"><Input /></Form.Item></Col>
          <Col xs={24} md={12}><Form.Item name="transitShippingFeeSettled" label="中转费结算" valuePropName="checked"><Switch checkedChildren="已结算" unCheckedChildren="未结算" /></Form.Item></Col>
          <Col span={24}><Form.Item name="remark" label="采购备注"><Input.TextArea rows={3} /></Form.Item></Col>
        </Row>
      </Form>
    </div>
    <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 10, display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 20px calc(12px + env(safe-area-inset-bottom))", background: "#fff", borderTop: "1px solid #f0f0f0", boxShadow: "0 -4px 12px rgba(15, 23, 42, 0.04)" }}>
      {isEdit && ["pending_pickup", "in_transit"].includes(editRecord?.status) && <Button type="primary" icon={<InboxOutlined />} onClick={() => onInbound?.(editRecord)}>入库</Button>}
    </div>
  </Drawer>;
}
